import type { QueryClient } from '@tanstack/react-query'
import type {
  ChatMessageResponse,
  DirectMessageResponse,
  DirectMessageThreadResponse,
  GameResponse,
  ServerEvent,
  TeamChatResponse,
} from '@warcarp/api-types'
import { appendChatMessage, updateChatMessageReactions } from './chatCache'
import {
  appendDirectMessage,
  dmPartnerId,
  dmThreadKey,
  dmThreadsKey,
  updateDirectMessageReactions,
} from './directMessages'
import { appendTeamMessage, teamChatKey } from './teamChat'
import { reconcileGameFrame, reconcileGameStatePatch } from './reconcileGameFrame'
import { publishYourTurn } from './notificationBanner'
import { refreshTurnGames } from './refreshTurnGames'

/**
 * Apply one already-parsed server frame to the client caches.
 *
 * Keeping this reducer separate from the socket lifecycle makes the important
 * ordering and private-channel rules testable without a native WebSocket or a
 * React renderer. The socket is responsible for transport concerns; this is
 * the single place where an inbound frame is allowed to mutate game caches.
 */
export function applyGameSocketEvent({
  gameId,
  event,
  qc,
  currentUserId,
  onYourTurn = publishYourTurn,
  onDirectMessage,
}: {
  gameId: string
  event: ServerEvent
  qc: QueryClient
  currentUserId?: string
  onYourTurn?: typeof publishYourTurn
  onDirectMessage?: (msg: DirectMessageResponse) => void
}): void {
  if (event.type === 'SUPPORT_UPDATED') {
    void qc.invalidateQueries({ queryKey: ['support'] })
    return
  }
  if (event.type === 'GAME_UPDATED' || event.type === 'GAME_COMPLETE') {
    // Frames can arrive late when a socket reconnects or an HTTP response
    // races the WS. Reconciliation discards older state before it can roll
    // back eliminations, phase, armies, or the append-only event pointer.
    qc.setQueryData<GameResponse>(['games', gameId], (old) => reconcileGameFrame(old, event.game))
    refreshTurnGames(qc, qc.getQueryData<GameResponse>(['games', gameId])!)
  } else if (event.type === 'GAME_STATE_PATCH') {
    // Intermediate bot-loop step. Preserve the cached player/invitation
    // metadata while applying only a newer state patch.
    qc.setQueryData<GameResponse>(['games', gameId], (old) => {
      if (!old) return old
      return reconcileGameStatePatch(old, event.state)
    })
  } else if (event.type === 'TURN_CHANGED') {
    void qc.invalidateQueries({ queryKey: ['games'], exact: true })
    qc.setQueryData<GameResponse>(['games', gameId], (old) => {
      if (!old) return old
      return {
        ...old,
        currentPlayerIndex: event.currentPlayerIndex,
        currentTurn: event.currentTurn,
      }
    })
  } else if (event.type === 'YOUR_TURN') {
    // Includes the zero-waiting-games case; the menu observes this same list.
    void qc.invalidateQueries({ queryKey: ['games'], exact: true })
    // This event arrived through the authenticated user's private channel.
    // Never put it on the game-wide cache path.
    onYourTurn({
      gameId: event.gameId,
      gameName: event.gameName,
      turn: event.turn,
      occurredAt: event.occurredAt,
    })
  } else if (event.type === 'PRESENCE_UPDATE') {
    qc.setQueryData<string[]>(['games', gameId, 'presence'], event.online)
    qc.setQueryData<string[]>(['games', gameId, 'presence-live'], event.live)
  } else if (event.type === 'CHAT_MESSAGE') {
    qc.setQueryData<ChatMessageResponse[]>(
      ['games', gameId, 'chat'],
      (old) => appendChatMessage(old, event.message),
    )
  } else if (event.type === 'CHAT_REACTION_UPDATED') {
    qc.setQueryData<ChatMessageResponse[]>(
      ['games', event.gameId, 'chat'],
      (old) => updateChatMessageReactions(old, event.messageId, event.reactions),
    )
  } else if (event.type === 'DIRECT_MESSAGE') {
    // A per-user socket can deliver a DM for a different game, so key caches
    // off the message's gameId and conversation partner rather than gameId.
    const dm = event.message
    const partner = currentUserId ? dmPartnerId(dm, currentUserId) : dm.fromUserId
    qc.setQueryData<DirectMessageThreadResponse>(dmThreadKey(dm.gameId, partner), (old) =>
      appendDirectMessage(old, dm),
    )
    void qc.invalidateQueries({ queryKey: dmThreadsKey(dm.gameId) })
    // Do not toast the sender's echo; only the addressed recipient gets the
    // in-app message notice.
    if (currentUserId && dm.toUserId === currentUserId) onDirectMessage?.(dm)
  } else if (event.type === 'TEAM_MESSAGE') {
    // Team chat arrives only on this user's private channel, possibly for a
    // different game than this socket's, so key the cache off the message.
    // The thread cache also drives the team bubble's unread badge.
    qc.setQueryData<TeamChatResponse>(teamChatKey(event.message.gameId), (old) =>
      appendTeamMessage(old, event.message),
    )
  } else if (event.type === 'DIRECT_MESSAGE_REACTION_UPDATED') {
    const partner = currentUserId === event.fromUserId ? event.toUserId : event.fromUserId
    qc.setQueryData<DirectMessageThreadResponse>(
      dmThreadKey(event.gameId, partner),
      (old) => updateDirectMessageReactions(old, event.messageId, event.reactions),
    )
  }
}
